import {Component, EventEmitter, OnInit, Output, signal, ViewChild, ElementRef, HostListener, Inject, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {HttpErrorResponse, HttpStatusCode} from "@angular/common/http";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {CustomDatePipe, ResponseHttpSuccess, CustomMessageService, UtilService, LocalStorageService} from "library";
import {CategoryRequestService} from "projects/store/src/app/services/api/v1/category-request.service";
import {UserValidatorService} from "projects/store/src/app/services/api/v1/user-validator.service";
import {environment} from "projects/store/src/environments/environment";
import {RequestService} from "projects/store/src/app/services/api/v1/request.service";
import {LocalStorageKey} from "projects/store/src/app/enums";
import {PixelService} from "projects/store/src/app/services/api/v1/pixel.service";

export enum Steps {
  'simulation' = 'simulation',
  'results' = 'results',
  'form' = 'form',
}

@Component({
  selector: 'app-sales-simulator-kredicash',
  templateUrl: './sales-simulator-kredicash.component.html',
  providers: [CustomDatePipe],
  styles: [`
    .iframe-container {
      width: 100%;
      height: 100%;
      border: none;
      overflow: hidden;
    }

    .iframe-container iframe {
      border: none;
      width: 100%;
      transition: height 0.3s ease;
    }

    .kredicash-embedded-wrapper.is-embedded {
      /* Styles specific to iframe mode */
      padding: 0;
      margin: 0;
    }
  `]
})
export class SalesSimulatorKredicashComponent implements OnInit{
  products: any = signal<any>([]);
  minPrice = signal<number>(1000000000);
  maxPrice = signal<number>(0);
  minDay = signal<number>(15);
  maxDay = signal<number>(0);
  prices: any = signal<any>([]);
  isSubmitted = signal<boolean>(false);
  isLoading = signal<boolean>(false);
  simulationsResults = signal<any>({});
  dayOptions : any[] = [];
  selectedProduct: any;
  selectedPrice!: number;
  selectedDay!:number;
  country = environment.global.country;
  form!: FormGroup;

  step = Steps.simulation;
  @Output() nextStepEvent = new EventEmitter();

  // Iframe properties
  @ViewChild('simulatorIframe') simulatorIframe!: ElementRef<HTMLIFrameElement>;
  iframeSrc: SafeResourceUrl;
  iframeHeight = 800;

  constructor(private categoryRequestService: CategoryRequestService,
              private utilService: UtilService,
              private customMessageService: CustomMessageService,
              private customDatePipe: CustomDatePipe,
              private userValidatorService: UserValidatorService,
              private requestService: RequestService,
              private localStorageService: LocalStorageService,
              private metaPixelService: PixelService,
              private sanitizer: DomSanitizer,
              @Inject(PLATFORM_ID) private platformId: Object)  {
    this.form = new FormGroup({
      amount: new FormControl(''),
      quota_quantity: new FormControl(''),
      salary: new FormControl(null, Validators.required),
      frecuency: new FormControl('')
    });
    this.requestService.step.set(1);

    // Set up iframe src with embedded component
    const embeddedUrl = `${window.location.origin}${window.location.pathname}#/simulator-kredicash-embedded?categoryId=${this.requestService.categoryId}`;
    this.iframeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(embeddedUrl);
  }

  ngOnInit() {
    // Setup message listener for iframe communication
    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener('message', this.handleIframeMessage.bind(this));
    }

    this.load();
  }

  load(){
    this.utilService.enablePageLoading();
    this.localStorageService.remove(LocalStorageKey.simulation);
    this.categoryRequestService.getProductsByCategory(this.requestService.categoryId).subscribe({
      next: (resp: ResponseHttpSuccess)=> {
        if (resp.status === HttpStatusCode.Ok) {
          this.products.set(resp.body.data.list)
          this.onExtractMinMaxPrices();
        }else{
          this.customMessageService.responseNoticeHandler(resp);
          this.utilService.disablePageLoading();
        }
      },
      error: (err: HttpErrorResponse) => {
        this.customMessageService.responseNoticeHandler(err);
        this.utilService.disablePageLoading();
      }
    })
  }

  onExtractMinMaxPrices(){
    let availableDays: number[] = [];
    this.products().forEach( (row: any) => {
      row.quotes.forEach((row1: any) => {
        this.minPrice.set( row1.prices.suggested < this.minPrice() ? row1.prices.suggested : this.minPrice() );
        this.maxPrice.set(row1.prices.suggested > this.maxPrice() ? row1.prices.suggested : this.maxPrice());
        const currentDay = row1.quota.quantity * 15;
        availableDays.push(currentDay)

        if (!this.prices().includes(row1.prices.suggested)) {
          this.prices.update(  (prices: any) => [...prices, row1.prices.suggested] )
        }

        if(row1.prices.suggested == this.maxPrice()) {
          this.selectedProduct = row;
        }
      });
    });
    const uniqueDays = availableDays
      .filter((item, index) => availableDays.indexOf(item) === index)
      .sort((a, b) => Number(a) - Number(b));
    this.minDay.set(uniqueDays[0])
    this.maxDay.set(uniqueDays[uniqueDays.length - 1])
    this.selectedPrice = this.maxPrice();
    this.selectedDay = this.maxDay();
    this.onExtractDaysForSelected();

    this.utilService.disablePageLoading();
  }

  onExtractDaysForSelected(){
    const min = this.minDay();
    const max = this.maxDay();
    for (let i =  min; i <= max; i+=60){
      this.dayOptions.push(i);
    }

  }

  simulate(){
    this.isSubmitted.set(true);
    if (this.form.invalid){
      return;
    }

    this.form.patchValue({
      amount: this.selectedPrice,
      quota_quantity: this.selectedDay / 15,
      frecuency: 15
    });

    this.categoryRequestService.simulateCredit(this.requestService.categoryId, this.form.value).subscribe({
      next: (resp: ResponseHttpSuccess)=> {
        if (resp.status === HttpStatusCode.Ok) {
          this.simulationsResults.set(resp.body.data);
          this.step = Steps.results;
          this.isSubmitted.set(false);
          console.log(this.simulationsResults());
        }else{
          this.customMessageService.responseNoticeHandler(resp);

        }
      },
      error: (err: HttpErrorResponse) => {
        this.customMessageService.responseNoticeHandler(err);

      }
    });
  }

  onSlideEnd(price: any) {
    if( ! this.prices().includes(price)) {
      let previousPrice = this.selectedProduct.quotes[0].prices.suggested
      // TODO mejorar, deberia detenerse cuando se cumpla la condicion
      let priceTemp: any;
      if(price < previousPrice) {
        this.prices().sort((a: any, b: any) => b - a).forEach((row: any) => {
          if(row < price && priceTemp == undefined) {
            priceTemp = row;
          }
        });
      }else{
        this.prices().sort((a: any, b: any) => a - b).forEach((row: any) => {
          if(row > price && priceTemp == undefined) {
            priceTemp = row;
          }
        });
      }

      if(priceTemp) {
        this.selectedPrice = priceTemp;
      }
    }
    this.selectedProduct = this.getProductByPrice(this.selectedPrice);
  }

  getProductByPrice(price: number) {
    let product = null;
    this.products().forEach((row: any) => {
      row.quotes.forEach((row1: any) => {
        if(row1.prices.suggested === price) {
          row.quotes.sort((a: any, b: any) => a.quota.quantity - b.quota.quantity);
          product = row;
        }
      });
    });
    return product;
  }

  onSave() {
    this.metaPixelService.setLoanData(this.selectedPrice, 15, this.selectedDay / 15);
    this.metaPixelService.trackEvent('InitiateCheckout');
    this.localStorageService.set(LocalStorageKey.simulation, {...this.form.value, ...this.simulationsResults()});
    this.onNextStep();
  }
  onPreviousStep(step: string){
    this.step = Steps[step as Steps];

  }

  onNextStep() {
    this.nextStepEvent.emit();
  }

  // Iframe communication methods
  onIframeLoad() {
    // Send initialization data to iframe when it loads
    if (this.simulatorIframe?.nativeElement?.contentWindow) {
      setTimeout(() => {
        this.postMessageToIframe('setParams', {
          categoryId: this.requestService.categoryId,
          salary: this.form.get('salary')?.value
        });
      }, 1000); // Give iframe time to initialize
    }
  }

  handleIframeMessage(event: MessageEvent) {
    // Only accept messages from our own origin for security
    if (event.origin !== window.location.origin) {
      return;
    }

    if (event.data && typeof event.data === 'object') {
      switch (event.data.type) {
        case 'kredicash-simulator-ready':
          // Iframe is ready, send initial parameters
          this.postMessageToIframe('setParams', {
            categoryId: this.requestService.categoryId,
            salary: this.form.get('salary')?.value
          });
          break;

        case 'kredicash-simulator-resize':
          // Update iframe height
          if (event.data.data && event.data.data.height) {
            this.iframeHeight = Math.max(event.data.data.height + 50, 600);
          }
          break;

        case 'kredicash-simulator-nextStep':
          // Handle next step from embedded simulator
          if (event.data.data && event.data.data.simulationData) {
            // Update local data with simulation results
            const data = event.data.data.simulationData;
            this.simulationsResults.set(data);
            this.selectedPrice = data.amount || this.selectedPrice;
            this.selectedDay = (data.quota_quantity || 1) * 15;

            // Update form with received data
            this.form.patchValue(data);

            // Proceed to results step
            this.step = Steps.results;
          }
          break;
      }
    }
  }

  postMessageToIframe(type: string, data: any) {
    if (this.simulatorIframe?.nativeElement?.contentWindow) {
      this.simulatorIframe.nativeElement.contentWindow.postMessage({
        type: type,
        data: data
      }, window.location.origin);
    }
  }

  get f(){
    return this.form.controls;
  }

  protected readonly Steps = Steps;

}
